import { createSignal, createMemo } from 'solid-js';
import { createStore, reconcile, unwrap } from 'solid-js/store';

// src/index.ts
function makeAbortable(options = {}) {
  let controller;
  let timeout;
  const abort = (reason) => {
    timeout && clearTimeout(timeout);
    controller?.abort(reason);
  };
  const signal = () => {
    if (!options.noAutoAbort && controller?.signal.aborted === false) {
      abort("retry");
    }
    controller = new AbortController();
    if (options.timeout) {
      timeout = setTimeout(() => abort("timeout"), options.timeout);
    }
    return controller.signal;
  };
  return [
    signal,
    abort,
    (err) => {
      if (err.name === "AbortError") {
        return void 0;
      }
      throw err;
    }
  ];
}
var mapEntries = (entries) => entries.map((entry) => entry.map(serializer).join(":"));
var serializer = (req) => (
  // non-objects and null allow for string coercion
  !req || typeof req !== "object" ? req + "" === req ? `"${req}"` : req + "" : (
    // serializing the array items allows for string coercion: [1,2,3] == "[1,2,3]"
    Array.isArray(req) ? `[${req.map(serializer)}]` : (
      // Headers and maps support an entries method giving an entries iterator,
      // otherwise use Object.entries()s
      `{${mapEntries(
        typeof req.entries === "function" ? [...req.entries()] : Object.entries(req)
      )}}`
    )
  )
);
var cache = {};
function makeCache(fetcher, options) {
  const [expired, setExpired] = createSignal();
  const [serialize, deserialize] = options?.serialize || [JSON.stringify, JSON.parse];
  const localCache = options?.cache || cache;
  const key = options?.storageKey || "solid-cache";
  const save = () => {
    try {
      options?.storage?.setItem(key, serialize(localCache));
    } catch (e) {
    }
  };
  const expireTimeout = typeof options?.expires === "function" ? options.expires : () => options?.expires || 3e5;
  if (options?.storage) {
    try {
      Object.assign(localCache, deserialize(options.storage.getItem(key) || "{}"));
    } catch (e) {
    }
  }
  const sourceHash = options?.sourceHash || serializer;
  return [
    async (s, info) => {
      const hash = sourceHash(s);
      const cachedEntry = Object.hasOwn(localCache, hash) ? localCache[hash] : void 0;
      const now = +/* @__PURE__ */ new Date();
      if (cachedEntry && (cachedEntry.ts || now) >= now) {
        return cachedEntry.data;
      }
      const response = await fetcher(s, info);
      const entry = { source: s, data: response };
      const expiryDelay = expireTimeout(entry);
      entry.ts = now + expiryDelay;
      localCache[hash] = entry;
      save();
      setTimeout(() => {
        if (Object.hasOwn(localCache, hash)) {
          delete localCache[hash];
          save();
          setExpired(entry);
        }
      }, expiryDelay);
      return response;
    },
    Object.assign(
      (s = true) => {
        delete localCache[sourceHash(s)];
        save();
      },
      {
        all: () => {
          Object.keys(localCache).forEach((key2) => {
            delete localCache[key2];
          });
          save();
        }
      }
    ),
    expired
  ];
}
function makeRetrying(fetcher, options = {}) {
  const delay = options.delay ?? 5e3;
  let retries = options.retries || 3;
  const retrying = async (k, info) => {
    try {
      return await fetcher(k, info);
    } catch (error) {
      if (retries-- > 0) {
        delay && await new Promise((resolve) => setTimeout(resolve, delay));
        return retrying(k, info);
      } else {
        retries = options.retries || 3;
        throw error;
      }
    }
  };
  return retrying;
}
function toArray(item) {
  return Array.isArray(item) ? item : item ? [item] : [];
}
function createAggregated(res, initialValue) {
  return createMemo((previous) => {
    const current = res();
    return current == null && previous == null ? previous : Array.isArray(previous || current) ? [...toArray(previous), ...toArray(current)] : typeof (previous || current) === "object" ? { ...previous, ...current } : typeof previous === "string" || typeof current === "string" ? (previous?.toString() || "") + (current || "") : previous ? [previous, current] : [current];
  }, initialValue);
}
function createDeepSignal(v) {
  const [store, setStore] = createStore([v]);
  return [
    () => store[0],
    (update) => (setStore(0, reconcile(typeof update === "function" ? update(unwrap(store[0])) : v)), store[0])
  ];
}

export { cache, createAggregated, createDeepSignal, makeAbortable, makeCache, makeRetrying, serializer };
